import { type FormEvent, type ReactNode, useMemo, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageHero from '@/components/feature/PageHero';

type SubmitStatus = 'idle' | 'confirm' | 'submitting' | 'success' | 'error';

type FormDataState = {
  name: string;
  company: string;
  email: string;
  phone: string;
  inquiry_type: string;
  message: string;
  privacy: boolean;
};

const initialFormData: FormDataState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  inquiry_type: '',
  message: '',
  privacy: false
};

function Modal({
  isOpen,
  title,
  children,
  actions,
  closeOnBackdrop = true,
  onClose
}: {
  isOpen: boolean;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  closeOnBackdrop?: boolean;
  onClose?: () => void;
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4"
      onClick={() => {
        if (closeOnBackdrop && onClose) {
          onClose();
        }
      }}
    >
      <div
        className="w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="mb-4 text-2xl font-semibold text-slate-800">{title}</h3>
        <div className="text-slate-700">{children}</div>
        {actions && <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">{actions}</div>}
      </div>
    </div>
  );
}

export default function ContactPage() {
  const [formData, setFormData] = useState<FormDataState>(initialFormData);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [dialogMessage, setDialogMessage] = useState('');

  const confirmationItems = useMemo(
    () => [
      { label: 'お名前', value: formData.name },
      { label: '会社名・屋号', value: formData.company || '未入力' },
      { label: 'メールアドレス', value: formData.email },
      { label: '電話番号', value: formData.phone || '未入力' },
      { label: 'お問い合わせ種別', value: formData.inquiry_type || '未選択' },
      { label: 'お問い合わせ内容', value: formData.message }
    ],
    [formData]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;

    if (type === 'checkbox') {
      const checked = (event.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.privacy) {
      setDialogMessage('プライバシーポリシーへの同意が必要です。');
      setSubmitStatus('error');
      return false;
    }

    if (formData.message.length > 500) {
      setDialogMessage('お問い合わせ内容は500文字以内で入力してください。');
      setSubmitStatus('error');
      return false;
    }

    return true;
  };

  const handleOpenConfirm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!event.currentTarget.reportValidity()) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSubmitStatus('confirm');
  };

  const handleConfirmSubmit = async () => {
    setSubmitStatus('submitting');

    try {
      const data = new URLSearchParams();
      data.append('name', formData.name);
      data.append('company', formData.company);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('inquiry_type', formData.inquiry_type);
      data.append('message', formData.message);
      data.append('privacy', formData.privacy ? '同意する' : '');

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString()
      });

      const resultText = await response.text();

      if (response.ok && resultText.trim() === 'OK') {
        setFormData(initialFormData);
        setDialogMessage('お問い合わせありがとうございます。担当者より3営業日以内にご連絡いたします。');
        setSubmitStatus('success');
        return;
      }

      setDialogMessage('送信に失敗しました。しばらくしてから再度お試しください。');
      setSubmitStatus('error');
    } catch (error) {
      console.error('送信エラー:', error);
      setDialogMessage('通信エラーが発生しました。しばらくしてから再度お試しください。');
      setSubmitStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <PageHero label="Contact" title="お問い合わせ" subtitle="お気軽にご相談ください。担当者よりご連絡いたします。" />

      <section className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <form
            id="contact-form"
            onSubmit={handleOpenConfirm}
            className="bg-sky-50/50 rounded-2xl p-8 md:p-10 space-y-6"
          >
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                お名前 <span className="text-sky-400">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="山田 花子"
                className="w-full h-11 px-4 rounded-lg border border-sky-100 bg-white text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-sky-300 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">会社名・屋号</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="株式会社〇〇"
                className="w-full h-11 px-4 rounded-lg border border-sky-100 bg-white text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-sky-300 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                メールアドレス <span className="text-sky-400">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="example@email.com"
                className="w-full h-11 px-4 rounded-lg border border-sky-100 bg-white text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-sky-300 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">電話番号</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="090-0000-0000"
                className="w-full h-11 px-4 rounded-lg border border-sky-100 bg-white text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-sky-300 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">お問い合わせ種別</label>
              <select
                name="inquiry_type"
                value={formData.inquiry_type}
                onChange={handleChange}
                className="w-full h-11 px-4 rounded-lg border border-sky-100 bg-white text-sm text-slate-700 focus:outline-none focus:border-sky-300 transition-colors cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="マーケティング支援">マーケティング支援について</option>
                <option value="コンサルティング">コンサルティングについて</option>
                <option value="講座・セミナー">講座・セミナーについて</option>
                <option value="その他">その他</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                お問い合わせ内容 <span className="text-sky-400">*</span>
              </label>
              <textarea
                name="message"
                required
                rows={5}
                maxLength={500}
                value={formData.message}
                onChange={handleChange}
                placeholder="ご相談内容をご記入ください（500文字以内）"
                className="w-full px-4 py-3 rounded-lg border border-sky-100 bg-white text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:border-sky-300 transition-colors resize-none"
              ></textarea>
              <p className="text-xs text-slate-400 text-right mt-1">{formData.message.length}/500</p>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="privacy"
                name="privacy"
                checked={formData.privacy}
                onChange={handleChange}
                className="mt-0.5 w-4 h-4 rounded border-sky-200 text-sky-400 cursor-pointer accent-sky-400"
              />
              <label htmlFor="privacy" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                <a href="/privacy-policy" className="text-sky-400 underline hover:text-sky-500">
                  プライバシーポリシー
                </a>
                に同意する
              </label>
            </div>

            <button
              type="submit"
              disabled={submitStatus === 'submitting'}
              className="w-full h-12 rounded-full bg-sky-300 text-white text-sm font-medium hover:bg-sky-400 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
            >
              入力内容を確認する
            </button>
          </form>
        </div>
      </section>

      <Footer />

      <Modal
        isOpen={submitStatus === 'confirm'}
        title="この内容で送信しますか？"
        onClose={() => setSubmitStatus('idle')}
        actions={
          <>
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="px-6 py-3 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
            >
              戻る
            </button>
            <button
              type="button"
              onClick={handleConfirmSubmit}
              className="px-6 py-3 rounded-full bg-sky-300 text-white hover:bg-sky-400 transition-colors"
            >
              送信する
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {confirmationItems.map((item) => (
            <div key={item.label} className="rounded-2xl bg-sky-50 px-4 py-3">
              <div className="text-xs font-medium text-slate-400">{item.label}</div>
              <div className="mt-1 text-sm font-medium text-slate-700 whitespace-pre-wrap">{item.value}</div>
            </div>
          ))}
          <div className="rounded-2xl bg-sky-50 px-4 py-3">
            <div className="text-xs font-medium text-slate-400">プライバシーポリシー同意</div>
            <div className="mt-1 text-sm font-medium text-slate-700">同意する</div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'submitting'} title="送信中です" closeOnBackdrop={false}>
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-sky-100 border-t-sky-400"></div>
          <p className="text-sm font-medium text-slate-600">お問い合わせ内容を送信しています。しばらくお待ちください。</p>
        </div>
      </Modal>

      <Modal
        isOpen={submitStatus === 'success'}
        title="送信完了"
        onClose={() => setSubmitStatus('idle')}
        actions={
          <button
            type="button"
            onClick={() => setSubmitStatus('idle')}
            className="px-6 py-3 rounded-full bg-sky-300 text-white hover:bg-sky-400 transition-colors"
          >
            閉じる
          </button>
        }
      >
        <p className="text-sm font-medium text-slate-600">{dialogMessage}</p>
      </Modal>

      <Modal
        isOpen={submitStatus === 'error'}
        title="送信できませんでした"
        onClose={() => setSubmitStatus('idle')}
        actions={
          <button
            type="button"
            onClick={() => setSubmitStatus('idle')}
            className="px-6 py-3 rounded-full bg-sky-300 text-white hover:bg-sky-400 transition-colors"
          >
            閉じる
          </button>
        }
      >
        <p className="text-sm font-medium text-slate-600">{dialogMessage}</p>
      </Modal>
    </div>
  );
}
